/**
 * Where a source column can go, built from the registry: core animal and place fields, module
 * attributes, and the fields of event types that are about one animal. The same vocabulary drives
 * the heuristic mapper, the AI mapper's allowed answers and the review screen's choices.
 */
import type { Dimension, EventTypeDef, Field, Fields, Registry } from "../../../contract";
import { UNITS } from "../../../contract";
import { normalizeHeader } from "./table";
import type { TableFamily } from "./types";

export type TargetKind =
  | "key" | "text" | "tag" | "date" | "sex" | "status" | "origin" | "bool" | "number" | "quantity" | "enum"
  | "place" | "group" | "parent" | "parent_key" | "unit" | "place_kind" | "keep" | "skip";

export type TargetGroup = "identity" | "where" | "family" | "attributes" | "records" | "place" | "event" | "other";

export interface Target {
  id: string;
  /** English catalog key. Event targets also carry `prefix`, the event type's label. */
  label: string;
  prefix?: string;
  group: TargetGroup;
  kind: TargetKind;
  synonyms: string[];
  /** Stored as written ("exact") rather than converted ("translated"). */
  exact: boolean;
  /** More than one column may use it (keep, skip). */
  many?: boolean;
  dimension?: Dimension;
  units?: readonly string[];
  options?: Record<string, string>;
  eventType?: string;
  field?: string;
  attr?: string;
  required?: boolean;
  /** Money and prices never land where crew without finance access could read them. */
  money?: boolean;
  /** The heading must contain a whole synonym; a word of one ("Result" of "AI result") isn't enough. */
  strict?: boolean;
}

const words = (...list: string[]) => list.map(normalizeHeader);

const COMMON: Target[] = [
  { id: "source_id", label: "Source record ID", group: "identity", kind: "key", exact: true, synonyms: words("id", "system id", "animal id", "animal key", "record id", "internal id", "numero interno", "registro interno", "cm id", "cattlemax id", "uid", "key", "entity id", "animal number id", "row id", "codigo", "code", "id interno") },
  { id: "keep", label: "Keep as written", group: "other", kind: "keep", exact: true, many: true, synonyms: [] },
  { id: "skip", label: "Don't bring in", group: "other", kind: "skip", exact: true, many: true, synonyms: [] },
];

const ANIMAL_CORE: Target[] = [
  { id: "animal.tag", label: "Tag", group: "identity", kind: "tag", exact: true, synonyms: words("tag", "ear tag", "eartag", "tag number", "tag no", "tag #", "visual", "visual id", "visual tag", "arete", "numero de arete", "no arete", "management tag", "mgmt tag", "animal tag", "cow tag", "calf tag", "number", "no", "tag id", "ear tag number", "chapa", "caravana") },
  { id: "animal.name", label: "Name", group: "identity", kind: "text", exact: true, synonyms: words("name", "nombre", "animal name", "registered name", "reg name") },
  { id: "animal.eid", label: "EID", group: "identity", kind: "text", exact: true, synonyms: words("eid", "rfid", "electronic id", "840", "840 tag", "eid tag", "chip", "bolo", "numero electronico", "id electronico", "nues") },
  { id: "animal.registration", label: "Registration number", group: "identity", kind: "text", exact: true, synonyms: words("registration", "registration number", "reg", "reg no", "reg number", "registro", "numero de registro", "registration no", "herd book") },
  { id: "animal.sex", label: "Sex", group: "identity", kind: "sex", exact: false, synonyms: words("sex", "sexo", "gender", "sex class", "class", "clase", "type", "tipo", "category", "categoria", "kind", "animal type") },
  { id: "animal.castrated", label: "Castrated", group: "identity", kind: "bool", exact: false, synonyms: words("castrated", "castrado", "steered", "cut") },
  { id: "animal.birth_date", label: "Birth date", group: "identity", kind: "date", exact: false, synonyms: words("birth date", "birthdate", "dob", "born", "born at", "born on", "date of birth", "birthday", "fecha de nacimiento", "nacimiento", "nacio", "f nacimiento", "fnac", "birth", "calving date of birth", "bd") },
  { id: "animal.breed", label: "Breed", group: "identity", kind: "text", exact: true, synonyms: words("breed", "raza", "breed code", "breed composition", "cruza") },
  { id: "animal.color", label: "Color", group: "identity", kind: "text", exact: true, synonyms: words("color", "colour", "color markings", "markings", "pelaje", "color y marcas", "hide color") },
  { id: "animal.status", label: "Status", group: "identity", kind: "status", exact: false, synonyms: words("status", "estado", "state", "disposition", "estatus", "situacion", "condition status", "herd status") },
  { id: "animal.origin", label: "Origin", group: "identity", kind: "origin", exact: false, synonyms: words("origin", "origen", "born or bought", "procedencia", "source type") },
  { id: "animal.notes", label: "Notes", group: "identity", kind: "text", exact: true, synonyms: words("notes", "note", "notas", "nota", "comments", "comment", "comentarios", "remarks", "observaciones", "observacion", "memo") },
  { id: "animal.dam", label: "Dam", group: "family", kind: "parent", exact: false, synonyms: words("dam", "dam tag", "dam id", "mother", "madre", "mama", "dam number", "arete madre", "madre arete", "cow", "dam name") },
  { id: "animal.sire", label: "Sire", group: "family", kind: "parent", exact: false, synonyms: words("sire", "sire tag", "sire id", "sire name", "father", "padre", "toro padre", "bull", "sire registration", "papa", "semental padre") },
  { id: "animal.dam_key", label: "Dam's source record ID", group: "family", kind: "parent_key", exact: false, synonyms: [] },
  { id: "animal.sire_key", label: "Sire's source record ID", group: "family", kind: "parent_key", exact: false, synonyms: [] },
  { id: "place", label: "Pasture or pen", group: "where", kind: "place", exact: false, synonyms: words("pasture", "potrero", "location", "place", "lugar", "pen", "corral", "current location", "paddock", "field", "ubicacion", "trap", "lot location", "pasture name", "site", "rancho") },
  { id: "group", label: "Group", group: "where", kind: "group", exact: false, synonyms: words("group", "grupo", "herd", "hato", "management group", "mgmt group", "current group", "lote", "lot", "string", "bunch") },
  { id: "unit", label: "Unit for this row's amounts", group: "other", kind: "unit", exact: false, synonyms: words("unit", "units", "unidad", "unidades", "uom", "measure") },
];

const PLACE_CORE: Target[] = [
  { id: "place.name", label: "Place name", group: "place", kind: "text", exact: true, synonyms: words("name", "official name", "place", "place name", "pasture", "pasture name", "label", "nombre", "nombre oficial", "potrero", "location", "lugar") },
  { id: "place.alias", label: "Also called", group: "place", kind: "text", exact: true, synonyms: words("nickname", "aka", "also called", "alias", "apodo", "otro nombre", "local name", "common name") },
  { id: "place.kind", label: "Kind of place", group: "place", kind: "place_kind", exact: false, synonyms: words("kind", "type", "tipo", "place type", "clase") },
  { id: "place.area", label: "Area", group: "place", kind: "quantity", dimension: "area", units: UNITS.area, exact: false, synonyms: words("area", "acres", "acreage", "hectares", "hectareas", "ha", "ac", "superficie", "size") },
  { id: "place.capacity", label: "Capacity (head)", group: "place", kind: "number", exact: false, synonyms: words("capacity", "capacidad", "head capacity", "carrying capacity", "max head") },
];

const MONEY_WORDS = words("price", "precio", "cost", "costo", "value", "valor", "purchase price", "sale price", "precio de compra", "precio de venta", "amount", "monto", "usd", "dollars", "pesos", "paid", "pagado");

export function isMoneyHeader(header: string): boolean {
  const h = normalizeHeader(header);
  return MONEY_WORDS.some((w) => h === w || h.split(" ").includes(w) || h.endsWith(` ${w}`)) || /\$/.test(header);
}

const SCALAR: Field["kind"][] = ["text", "tag", "number", "boolean", "date", "enum", "quantity"];

function fieldKind(f: Field): TargetKind | null {
  switch (f.kind) {
    case "text": return "text";
    case "tag": return "tag";
    case "number": return "number";
    case "boolean": return "bool";
    case "date": return "date";
    case "enum": return f.multiple ? null : "enum";
    case "quantity": return "quantity";
    default: return null;
  }
}

/** Single-animal event types whose required facts can come from a spreadsheet row. */
export function mappableEventTypes(registry: Registry): EventTypeDef[] {
  return [...registry.events.values()].filter((def) => {
    if (def.type === "core.note" || def.type.startsWith("data.")) return false;
    const animalRoles = def.subjects.filter((s) => s.kind === "animal");
    if (!animalRoles.length) return false;
    const otherRequired = def.subjects.filter((s) => s.required && s.kind !== "animal");
    if (otherRequired.length) return false;
    return Object.values(def.fields).every((f) => !f.required || SCALAR.includes(f.kind));
  });
}

/**
 * On an animal list a column names its record ("Weaning weight", "Calving ease"); a bare "Result" or
 * "Weight" could belong to any record, so only a table of one record type matches field names alone.
 */
function fieldTargets(def: EventTypeDef, spanish: (s: string) => string | undefined, prefix: string, qualified = false): Target[] {
  const out: Target[] = [];
  for (const [key, field] of Object.entries(def.fields)) {
    const kind = fieldKind(field);
    if (!kind) continue;
    const label = field.label;
    const names = (text: string, record: string) => normalizeHeader(text).includes(normalizeHeader(record));
    const syn = new Set<string>(qualified && !names(label, def.label) ? words(`${def.label} ${label}`) : words(label, ...(qualified ? [] : [key.replace(/_/g, " ")]), `${def.label} ${label}`));
    const es = spanish(label);
    const esType = spanish(def.label);
    if (es && (!qualified || (esType && names(es, esType)))) syn.add(normalizeHeader(es));
    if (esType && es) syn.add(normalizeHeader(`${es} ${esType}`));
    out.push({
      id: `${prefix}${key}`, label, prefix: def.label, group: "event", kind, exact: kind === "text" || kind === "tag",
      synonyms: [...syn], dimension: field.kind === "quantity" ? field.dimension : undefined,
      units: field.kind === "quantity" ? field.units ?? UNITS[field.dimension] : undefined,
      options: field.kind === "enum" ? field.optionLabels : undefined, eventType: def.type, field: key, required: field.required,
      money: false, strict: qualified,
    });
  }
  out.push({ id: `${prefix}@date`, label: "Date", prefix: def.label, group: "event", kind: "date", exact: false, synonyms: words(`${def.label} date`, `date ${def.label}`), eventType: def.type, field: "@date", strict: qualified });
  return out;
}

function attributeTargets(registry: Registry, entity: "animal" | "place", spanish: (s: string) => string | undefined): Target[] {
  const out: Target[] = [];
  for (const m of registry.modules) {
    const fields: Fields = m.attributes?.[entity] ?? {};
    for (const [key, field] of Object.entries(fields)) {
      const kind = fieldKind(field);
      if (!kind || (m.id === "data")) continue;
      const es = spanish(field.label);
      out.push({
        id: `attr:${m.id}.${key}`, label: field.label, group: "attributes", kind, exact: kind === "text" || kind === "tag",
        synonyms: words(field.label, key.replace(/_/g, " "), ...(es ? [es] : [])), attr: `${m.id}.${key}`,
        dimension: field.kind === "quantity" ? field.dimension : undefined, units: field.kind === "quantity" ? field.units ?? UNITS[field.dimension] : undefined,
        options: field.kind === "enum" ? field.optionLabels : undefined,
      });
    }
  }
  return out;
}

export interface TargetOptions {
  /** Spanish for a catalog key, so "Peso" finds a "Weight" field. */
  spanish?: (english: string) => string | undefined;
  /** For event tables: the chosen type. */
  eventType?: string | null;
}

/** The vocabulary for one table family. */
export function buildTargets(registry: Registry, family: TableFamily, opts: TargetOptions = {}): Target[] {
  const spanish = opts.spanish ?? (() => undefined);
  if (family === "places") return [...COMMON, ...PLACE_CORE, ...attributeTargets(registry, "place", spanish)];
  if (family === "events") {
    const def = opts.eventType ? registry.event(opts.eventType) : undefined;
    const base: Target[] = [
      ...COMMON,
      { ...ANIMAL_CORE[0], id: "animal.tag", label: "Animal's tag", group: "identity" },
      { id: "event.@date", label: "Date", group: "event", kind: "date", exact: false, synonyms: words("date", "fecha", "day", "dia", "when", "on", "date done", "event date", "fecha del evento") },
      ANIMAL_CORE.find((t) => t.id === "unit")!,
      { id: "event.@notes", label: "Notes", group: "event", kind: "text", exact: true, synonyms: words("notes", "note", "notas", "comments", "comentarios", "observaciones") },
    ];
    if (!def) return base;
    return [...base, ...fieldTargets(def, spanish, "event.").filter((t) => t.field !== "@date")];
  }
  if (family === "animals" || family === "v1_animals") {
    const events = mappableEventTypes(registry).flatMap((def) => fieldTargets(def, spanish, `event:${def.type}:`, true));
    return [...COMMON, ...ANIMAL_CORE, ...attributeTargets(registry, "animal", spanish), ...events];
  }
  return COMMON;
}

export function targetById(targets: Target[], id: string): Target | undefined {
  return targets.find((t) => t.id === id);
}

/** True when a heading is one we recognize for any family (helps find the header row). */
export function knownHeaderWords(registry: Registry, spanish?: (s: string) => string | undefined): (cell: string) => boolean {
  const all = new Set<string>();
  for (const family of ["animals", "places", "events"] as const) for (const t of buildTargets(registry, family, { spanish })) for (const s of t.synonyms) all.add(s);
  for (const def of mappableEventTypes(registry)) for (const t of fieldTargets(def, spanish ?? (() => undefined), "")) for (const s of t.synonyms) all.add(s);
  for (const def of registry.events.values()) all.add(normalizeHeader(def.label));
  return (cell: string) => all.has(normalizeHeader(cell));
}
